import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Layout } from '@/components/wiki/Layout';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClaimSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeader, refreshUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const [returnUrl, setReturnUrl] = useState(null);
  const sessionId = searchParams.get('session_id');
  const returnUrlParam = searchParams.get('return_url');
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessed.current) return;
    
    const checkStatus = async (attempts = 0) => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const response = await axios.get(`${API}/subscriptions/status/${sessionId}`, {
          headers: getAuthHeader()
        });

        if (response.data.payment_status === 'paid') {
          // Mark as processed to prevent duplicate toasts
          if (hasProcessed.current) return;
          hasProcessed.current = true;
          
          setStatus('success');
          setReturnUrl(response.data.return_url || returnUrlParam || '/');
          
          // Refresh user data to get updated claim credits
          if (refreshUser) {
            await refreshUser();
          }
          
          // Show success toast (only once)
          toast.success('Payment successful! You now have 1 claim credit available.', {
            duration: 5000,
          });
          
          // Redirect after a short delay
          setTimeout(() => {
            const targetUrl = response.data.return_url || returnUrlParam || '/';
            // Store a flag to show the claim alert on the target page
            sessionStorage.setItem('show_claim_alert', 'true');
            navigate(targetUrl);
          }, 3000);
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < 5) {
          // Continue polling
          setTimeout(() => checkStatus(attempts + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Status check error:', error);
        if (attempts < 5) {
          setTimeout(() => checkStatus(attempts + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkStatus();
  }, [sessionId, getAuthHeader, navigate, returnUrlParam, refreshUser]);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="claim-success">
        {status === 'checking' && (
          <div className="animate-pulse">
            <Loader2 className="h-16 w-16 mx-auto mb-6 text-[#D4AF37] animate-spin" />
            <h1 className="font-spectral text-2xl font-bold text-white mb-4">
              Verifying Payment...
            </h1>
            <p className="text-gray-400">Please wait while we confirm your payment.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="animate-fadeIn">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] mb-6">
              <CheckCircle className="h-10 w-10" />
            </div>
            <h1 className="font-spectral text-3xl font-bold text-white mb-4">
              Payment Successful!
            </h1>
            <p className="text-gray-400 mb-6">
              You now have <span className="text-[#D4AF37] font-semibold">1 claim credit</span> available to sponsor a page.
            </p>
            <div className="bg-[#1A1A1A] rounded-xl p-6 border border-[#252525] mb-8">
              <Sparkles className="h-8 w-8 mx-auto mb-4 text-[#D4AF37]" />
              <p className="text-gray-300">
                Redirecting you back to the page...
              </p>
              <p className="text-sm text-gray-500 mt-2">
                You can claim any page about yourself or your employer.
              </p>
            </div>
            <Button 
              onClick={() => navigate(returnUrl || '/')}
              className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0F0F0F]"
            >
              Continue Now
            </Button>
          </div>
        )}

        {status === 'pending' && (
          <div>
            <Loader2 className="h-16 w-16 mx-auto mb-6 text-[#D4AF37] animate-spin" />
            <h1 className="font-spectral text-2xl font-bold text-white mb-4">
              Payment Processing
            </h1>
            <p className="text-gray-400 mb-6">
              Your payment is still being processed. This may take a few moments.
            </p>
            <Button onClick={() => window.location.reload()} variant="outline" className="border-[#333333] text-gray-300">
              Check Again
            </Button>
          </div>
        )}

        {status === 'expired' && (
          <div>
            <h1 className="font-spectral text-2xl font-bold text-white mb-4">
              Session Expired
            </h1>
            <p className="text-gray-400 mb-6">
              This checkout session has expired. Please try again.
            </p>
            <Button onClick={() => navigate('/pricing')} className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0F0F0F]">
              Return to Pricing
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div>
            <h1 className="font-spectral text-2xl font-bold text-white mb-4">
              Something Went Wrong
            </h1>
            <p className="text-gray-400 mb-6">
              We couldn't verify your payment. Please contact support if the issue persists.
            </p>
            <Button onClick={() => navigate('/')} variant="outline" className="border-[#333333] text-gray-300">
              Return Home
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
